import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import SectionTrustBar from "../../components/feature/sections/SectionTrustBar";
import SectionAIBlock from "../../components/feature/sections/SectionAIBlock";
import SectionProcess from "../../components/feature/sections/SectionProcess";
import SectionWhyChooseUs from "../../components/feature/sections/SectionWhyChooseUs";
import SectionServiceAreas from "../../components/feature/sections/SectionServiceAreas";
import SectionTestimonials from "../../components/feature/sections/SectionTestimonials";
import SectionFAQ from "../../components/feature/sections/SectionFAQ";
import SectionFinalCTA from "../../components/feature/sections/SectionFinalCTA";

const caseResults = [
  {
    icon: "ri-government-line",
    category: "Medicare Compliance",
    title: "Government Audit Defense",
    desc: "Successfully defended a multi-specialty physician group in a CMS RAC audit involving $2.3M in alleged overpayments, negotiating a settlement that eliminated 85% of initial claims.",
    outcome: "Favorable Resolution",
  },
  {
    icon: "ri-file-shield-2-line",
    category: "Stark Law",
    title: "Physician Arrangement Restructuring",
    desc: "Restructured 14 physician employment arrangements for a regional health system to bring all compensation models into Stark Law compliance and eliminate significant regulatory exposure.",
    outcome: "Full Compliance Achieved",
  },
  {
    icon: "ri-exchange-funds-line",
    category: "Managed Care",
    title: "Payer Contract Renegotiation",
    desc: "Negotiated and restructured a major managed care contract for a Pennsylvania physician group, achieving a 22% increase in reimbursement rates and favorable contract terms.",
    outcome: "22% Rate Increase",
  },
  {
    icon: "ri-scales-3-line",
    category: "Litigation",
    title: "Administrative Proceeding Defense",
    desc: "Represented a hospital-employed physician in a state licensing board proceeding, achieving dismissal of all charges and full preservation of the physician's medical license.",
    outcome: "All Charges Dismissed",
  },
  {
    icon: "ri-shield-user-line",
    category: "OIG Compliance",
    title: "Corporate Integrity Agreement",
    desc: "Guided a health care organization through a voluntary self-disclosure and negotiated a Corporate Integrity Agreement with terms significantly more favorable than OIG's initial position.",
    outcome: "Reduced Liability Exposure",
  },
  {
    icon: "ri-building-4-line",
    category: "Health System Transaction",
    title: "Multi-State Acquisition",
    desc: "Provided comprehensive health law counsel for a multi-state health system acquisition spanning five states, navigating complex state and federal regulatory approval processes.",
    outcome: "Transaction Completed",
  },
];

const awards = [
  { year: "2025", title: "Best Lawyers in America", org: "Best Lawyers" },
  { year: "2024", title: "Top 5 Law Firm", org: "Legal Times" },
  { year: "2024", title: "Super Lawyers — Health Law", org: "Super Lawyers" },
  { year: "2023", title: "Top Rated Health Care Attorney", org: "Martindale-Hubbell AV Preeminent" },
  { year: "2023", title: "Leading Health Law Firm", org: "Chambers USA" },
  { year: "2022", title: "Excellence in Client Service", org: "BTI Client Service All-Stars" },
];

const resultsAIQuestions = [
  {
    q: "What kind of results does this law firm achieve?",
    a: "Alice G. Gosfield & Associates consistently achieves favorable results for clients in Medicare compliance audits, Stark Law restructurings, managed care contract negotiations, licensing board proceedings, and government investigations. Our 40+ years of specialized expertise translates into better outcomes for every client matter we handle.",
  },
  {
    q: "How does the firm approach complex legal matters?",
    a: "Our approach combines deep regulatory expertise with strategic thinking and clear communication. We assess each matter thoroughly, develop a targeted strategy, and execute with precision — whether resolving a compliance issue proactively or defending aggressively in adversarial proceedings.",
  },
  {
    q: "What recognition has the firm received?",
    a: "The firm has been consistently recognized among the nation's top health law practices, receiving recognition from Best Lawyers in America, Super Lawyers, Chambers USA, and Martindale-Hubbell (AV Preeminent) — the highest possible rating for legal ability and ethical standards.",
  },
];

const resultsFAQs = [
  {
    q: "How do you measure success in legal matters?",
    a: "Success depends on the client's specific goals — whether that's minimizing financial liability, achieving regulatory compliance, preserving a professional license, or closing a transaction. We define success at the outset of every engagement and measure our work against those specific client objectives.",
  },
  {
    q: "Can you share specific case outcomes?",
    a: "Due to attorney-client confidentiality, we cannot share specific client-identifiable details. The case examples shown represent the types of outcomes our clients have achieved, with identifying details changed to protect confidentiality.",
  },
  {
    q: "What types of cases does the firm handle most frequently?",
    a: "Our most common matters involve Medicare and Medicaid compliance, Stark Law and anti-kickback analysis, managed care contracting, HIPAA privacy counseling, and defense in government audits and investigations — reflecting the core regulatory challenges facing health care providers today.",
  },
  {
    q: "Does the firm take cases on contingency?",
    a: "Health law regulatory and compliance matters are generally handled on an hourly or retainer basis. We provide transparent fee structures and offer initial consultations to assess your situation and provide a clear understanding of the expected scope and cost of representation.",
  },
];

export default function ResultsPage() {
  return (
    <div className="font-inter">
      <Navbar />
      <main>
        {/* ① HERO */}
        <section className="relative min-h-[70vh] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/13e71f645dbe6f93510f5e117423c780.jpg"
              alt="Proven legal results — Gosfield &amp; Associates"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/94 via-[#0A1628]/72 to-[#0A1628]/30" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628]/60 to-transparent" />
          </div>
          <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-12 py-32 pt-44">
            <span className="block text-[11px] tracking-[0.25em] text-[#C9A961] uppercase font-inter font-medium mb-4">
              Track Record
            </span>
            <h1 className="text-5xl lg:text-[64px] font-playfair font-bold text-white leading-[1.06] max-w-2xl mb-6">
              Proven Results for<br />
              <span className="italic text-[#C9A961]">Our Clients</span>
            </h1>
            <p className="text-slate-300 font-inter text-lg max-w-xl mb-8 leading-relaxed">
              Four decades of favorable outcomes for physicians, health systems, and health care
              organizations navigating complex legal and regulatory challenges.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 bg-[#C9A961] hover:bg-[#b8954f] text-[#0A1628] font-inter font-bold px-7 py-3.5 rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-calendar-check-line" />
                Schedule Consultation
              </Link>
              <a
                href="tel:+12157352384"
                className="inline-flex items-center justify-center gap-2 border border-white/30 hover:border-[#C9A961] text-white hover:text-[#C9A961] font-inter font-medium px-7 py-3.5 rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line text-[#C9A961]" />
                (215) 735-2384
              </a>
            </div>
          </div>
        </section>

        {/* ② TRUST BAR */}
        <SectionTrustBar />

        {/* ③ CASE RESULTS */}
        <section className="bg-white py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-14">
              <div>
                <span className="block text-[11px] tracking-[0.25em] text-[#C9A961] uppercase font-inter font-medium mb-3">
                  Case Outcomes
                </span>
                <h2 className="text-4xl lg:text-5xl font-playfair font-bold text-[#0A1628] mb-3 leading-tight">
                  Client Success Stories
                </h2>
                <p className="text-slate-500 font-inter text-base max-w-xl">
                  Representative matters demonstrating the types of outcomes we achieve for our clients.
                  Details changed to protect confidentiality.
                </p>
              </div>
            </div>

            {/* Case results grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mb-14">
              {caseResults.map((c, i) => (
                <div
                  key={i}
                  className="flex flex-col p-7 rounded-xl bg-[#F8F7F4] border border-slate-100 hover:border-[#C9A961]/30 hover:bg-white transition-all duration-300"
                >
                  <div className="flex items-center justify-between mb-5">
                    <span className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#0A1628]">
                      <i className={`${c.icon} text-[#C9A961] text-lg`} />
                    </span>
                    <span className="text-xs font-inter font-semibold text-[#C9A961] bg-[#C9A961]/10 border border-[#C9A961]/20 px-2.5 py-1 rounded-full">
                      {c.outcome}
                    </span>
                  </div>
                  <div className="text-[10px] tracking-wider text-slate-400 font-inter uppercase mb-2">
                    {c.category}
                  </div>
                  <h3 className="font-playfair font-bold text-[#0A1628] text-lg mb-3 leading-snug">
                    {c.title}
                  </h3>
                  <p className="text-slate-500 text-sm font-inter leading-relaxed flex-1">{c.desc}</p>
                </div>
              ))}
            </div>

            {/* Awards section */}
            <div className="border-t border-slate-100 pt-14">
              <div className="text-center mb-10">
                <span className="block text-[11px] tracking-[0.25em] text-[#C9A961] uppercase font-inter font-medium mb-3">
                  Recognition &amp; Awards
                </span>
                <h2 className="text-3xl font-playfair font-bold text-[#0A1628]">
                  Nationally Recognized Excellence
                </h2>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                {awards.map((award, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-4 p-5 rounded-xl bg-[#F8F7F4] border border-slate-100"
                  >
                    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-[#C9A961]/15 border border-[#C9A961]/30 flex-shrink-0">
                      <i className="ri-medal-line text-[#C9A961] text-base" />
                    </span>
                    <div>
                      <div className="text-[10px] text-[#C9A961] font-inter font-semibold mb-0.5">
                        {award.year}
                      </div>
                      <div className="text-sm font-inter font-bold text-[#0A1628] leading-snug mb-1">
                        {award.title}
                      </div>
                      <div className="text-xs text-slate-400 font-inter">{award.org}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ④ AI ANSWER BLOCK */}
        <SectionAIBlock
          questions={resultsAIQuestions}
          title="About Our Results"
        />

        {/* ⑤ PROCESS */}
        <SectionProcess />

        {/* ⑥ WHY CHOOSE US */}
        <SectionWhyChooseUs />

        {/* ⑦ SERVICE AREAS */}
        <SectionServiceAreas />

        {/* ⑧ TESTIMONIALS */}
        <SectionTestimonials />

        {/* ⑨ FAQ */}
        <SectionFAQ
          faqs={resultsFAQs}
          title="Results &amp; Process FAQ"
        />

        {/* ⑩ FINAL CTA */}
        <SectionFinalCTA
          headline="Get the Results You Deserve Today"
          subheadline="Our track record speaks for itself. Let our experienced health law attorneys provide the strategic guidance needed to achieve a favorable outcome for your situation."
          ctaLabel="Schedule Your Consultation"
        />
      </main>
      <Footer />
    </div>
  );
}
